export const truncateDecimals = (
  value: number,
  decimals: number = 2
): string => {
  if (isNaN(value) || !isFinite(value)) {
    if (decimals === 0) {
      return "0";
    }
    return "0." + "0".repeat(decimals);
  }

  if (decimals === 0) {
    const wholeNumber = Math.floor(Math.abs(value));
    return (value < 0 ? "-" : "") + wholeNumber.toString();
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const multiplier = Math.pow(10, decimals);
  const truncated = Math.floor(absValue * multiplier) / multiplier;
  const result = isNegative ? -truncated : truncated;

  const resultStr = result.toString();
  const parts = resultStr.split(".");

  if (parts.length === 1) {
    return resultStr + "." + "0".repeat(decimals);
  }

  let decimalPart = parts[1];

  if (decimalPart.length > decimals) {
    decimalPart = decimalPart.substring(0, decimals);
  }

  if (decimalPart.length < decimals) {
    decimalPart = decimalPart + "0".repeat(decimals - decimalPart.length);
  }

  if (!decimalPart || decimalPart.length === 0) {
    return parts[0] + "." + "0".repeat(decimals);
  }

  return parts[0] + "." + decimalPart;
};

/**
 * Truncates to decimals then formats the integer part with spaces as thousand separators.
 * e.g. 67123.456 -> "67 123.45"
 */
export const formatWithThousandSpaces = (
  value: number,
  decimals: number = 2
): string => {
  const truncated = truncateDecimals(value, decimals);
  const [intPart, decPart] = truncated.split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return decPart != null ? `${withSpaces}.${decPart}` : withSpaces;
};

export const formatDynamicDecimals = (
  value: number,
  symbol?: string
): string => {
  const symbolUpper = symbol?.toUpperCase();

  if (symbolUpper === "BTC" || symbolUpper === "BITCOIN") {
    return truncateDecimals(value, 6);
  }

  return truncateDecimals(value, 2);
};

/**
 * Formats a number with significant digits, removing trailing zeros.
 * - For numbers >= 1000: max 2 decimal places
 * - For numbers >= 1: max 4 decimal places
 * - For numbers < 1: shows 4 significant digits after the first non-zero digit
 *
 * Examples:
 * - 0.00001 → "0.00001"
 * - 0.00111 → "0.0011"
 * - 0.123456 → "0.1235"
 * - 1234.56789 → "1234.57"
 */
export const formatWithSignificantDigits = (
  value: number,
  options: { minSignificant?: number; maxDecimals?: number } = {}
): string => {
  const { minSignificant = 4, maxDecimals = 8 } = options;

  if (isNaN(value) || !isFinite(value) || value === 0) {
    return "0";
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Large numbers (>= 1000): max 2 decimals
  if (absValue >= 1000) {
    const truncated = Math.floor(absValue * 100) / 100;
    let result = truncated.toString();
    // Remove trailing zeros after decimal
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
    return isNegative ? `-${result}` : result;
  }

  // Medium numbers (>= 1): max 4 decimals
  if (absValue >= 1) {
    const truncated = Math.floor(absValue * 10000) / 10000;
    let result = truncated.toString();
    // Remove trailing zeros after decimal
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
    return isNegative ? `-${result}` : result;
  }

  // Small numbers (< 1): find first significant digit and show minSignificant digits
  const absStr = absValue.toString();

  // Handle scientific notation
  if (absStr.includes("e")) {
    const formatted = absValue.toPrecision(minSignificant);
    const num = parseFloat(formatted);
    // Convert back to fixed notation if within range
    const decimalPlaces = Math.min(
      maxDecimals,
      Math.max(0, -Math.floor(Math.log10(num)) + minSignificant - 1)
    );
    let result = num.toFixed(decimalPlaces);
    // Remove trailing zeros
    result = result.replace(/\.?0+$/, "");
    return isNegative ? `-${result}` : result;
  }

  // Find position of first non-zero digit after decimal
  const decimalIndex = absStr.indexOf(".");
  if (decimalIndex === -1) {
    return isNegative ? `-${absStr}` : absStr;
  }

  const afterDecimal = absStr.substring(decimalIndex + 1);
  let firstNonZeroPos = 0;
  for (let i = 0; i < afterDecimal.length; i++) {
    if (afterDecimal.charAt(i) !== "0") {
      firstNonZeroPos = i;
      break;
    }
  }

  // Calculate how many decimal places we need
  const neededDecimals = Math.min(
    firstNonZeroPos + minSignificant,
    maxDecimals
  );

  // Truncate (not round) to the needed decimals
  const multiplier = Math.pow(10, neededDecimals);
  const truncated = Math.floor(absValue * multiplier) / multiplier;

  let result = truncated.toFixed(neededDecimals);

  // Remove trailing zeros after decimal point
  result = result.replace(/\.?0+$/, "");

  // Ensure we don't return just "0" for small values
  if (result === "0" && absValue > 0) {
    result = absValue.toPrecision(minSignificant);
    // Remove trailing zeros
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
  }

  return isNegative ? `-${result}` : result;
};

export const formatFiatWithTwoDecimals = (value: string | number): string => {
  if (!value && value !== 0) return "R 0.00";

  let numericValue: number;
  if (typeof value === "string") {
    const cleaned = value.replace(/R\s*|,|\s/g, "");
    numericValue = parseFloat(cleaned);
    if (isNaN(numericValue)) {
      return "R 0.00";
    }
  } else {
    numericValue = value;
  }

  const formatted = truncateDecimals(numericValue, 2);

  const parts = formatted.split(".");
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const decimalPart = parts[1] || "00";
  const paddedDecimal = decimalPart.padEnd(2, "0").substring(0, 2);

  return `R ${integerPart}.${paddedDecimal}`;
};
